import {ITopAutomatedTask} from "../TopAutomatedTasksView";

export interface IActivityData {
    count: number;
    timeSaved?: number;
    costSaved?: number;
    dailyDetails?: Array<{
        day: number;
        count: number;
        timeSaved: number;
        costSaved: number;
    }>;
}

export interface IActivitiesResponse {
    [activityName: string]: IActivityData;
}

/**
 * Processes the activities data from the ROI report response
 * and returns the top automated tasks sorted by time saved
 */
export const processTopAutomatedTasks = (
    activitiesData: IActivitiesResponse,
    roiConfig?: {
        activities?: {
            [key: string]: {
                hoursPerTask: number;
            };
        };
    }
): ITopAutomatedTask[] => {
    if (!activitiesData || typeof activitiesData !== "object") {
        return [];
    }

    const tasks: ITopAutomatedTask[] = [];

    Object.entries(activitiesData).forEach(([activityName, activityData]) => {
        // Calculate total time saved from daily details if available
        let totalTimeSaved = 0;
        let totalCostSaved = 0;
        let totalCount = 0;

        if (
            activityData.dailyDetails &&
            Array.isArray(activityData.dailyDetails) &&
            activityData.dailyDetails.length > 0
        ) {
            // Sum up all daily details
            totalTimeSaved = activityData.dailyDetails.reduce(
                (sum, detail) => sum + (detail.timeSaved || 0),
                0
            );
            totalCostSaved = activityData.dailyDetails.reduce(
                (sum, detail) => sum + (detail.costSaved || 0),
                0
            );
            totalCount = activityData.dailyDetails.reduce(
                (sum, detail) => sum + (detail.count || 0),
                0
            );
        } else {
            // Fallback to direct values
            totalTimeSaved = activityData.timeSaved || 0;
            totalCostSaved = activityData.costSaved || 0;
            totalCount = activityData.count || 0;

            // If no time saved but we have count and config, calculate it
            if (totalTimeSaved === 0 && totalCount > 0 && roiConfig?.activities?.[activityName]) {
                const hoursPerTask = roiConfig.activities[activityName].hoursPerTask;
                totalTimeSaved = totalCount * hoursPerTask;
            }
        }

        if (totalTimeSaved > 0) {
            tasks.push({
                name: activityName,
                displayName: getActivityDisplayName(activityName),
                count: totalCount,
                timeSaved: totalTimeSaved,
                costSaved: totalCostSaved,
            });
        }
    });

    // Sort by time saved (descending) and return top 5
    return tasks.sort((a, b) => b.timeSaved - a.timeSaved).slice(0, 5);
};

/**
 * Converts activity names to display-friendly names
 */
export const getActivityDisplayName = (activityName: string): string => {
    const displayNames: {[key: string]: string} = {
        featureProvisioning: "Feature Provisioning",
        capture: "Configuration Capture",
        backup: "Configuration Backup",
        audit: "Configuration Audit",
        osUpgrade: "OS Upgrade",
        deviceDiscovery: "Device Discovery",
        configurationDeployment: "Configuration Deployment",
        configurationAudit: "Configuration Audit",
        deviceBackup: "Device Backup",
        firmwareUpgrade: "Firmware Upgrade",
        complianceCheck: "Compliance Check",
        networkDiscovery: "Network Discovery",
    };

    return (
        displayNames[activityName] ||
        activityName
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (str) => str.toUpperCase())
            .trim()
    );
};

/**
 * Formats hours for display
 */
export const formatHours = (hours: number): string => {
    if (hours < 1) {
        return `${Math.round(hours * 60)} min`;
    }
    return `${Math.round(hours)} hrs`;
};

/**
 * Sample usage with the provided JSON structure:
 *
 * const roiResponse = {
 *   report: {
 *     activities: {
 *       featureProvisioning: { count: 70, dailyDetails: [...] },
 *       capture: { count: 25, dailyDetails: [...] },
 *       // ... other activities
 *     }
 *   },
 *   roiConfig: {
 *     activities: {
 *       featureProvisioning: { hoursPerTask: 4.2 },
 *       capture: { hoursPerTask: 0.5 },
 *       // ... other configs
 *     }
 *   }
 * };
 *
 * const topTasks = processTopAutomatedTasks(
 *   roiResponse.report.activities,
 *   roiResponse.roiConfig
 * );
 */
